import ClearIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  alpha,
  Autocomplete,
  autocompleteClasses,
  AutocompleteRenderInputParams,
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  PopperPlacementType,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  LOCKED_OPACITY,
  POPOVER_FOOTER_CLEARANCE,
  POPOVER_MAX_HEIGHT,
  POPOVER_MIN_HEIGHT,
  POPOVER_VIEWPORT_PADDING,
  SEARCH_VISIBLE_THRESHOLD,
} from '../../../constants/ui';
import { useFooterVisibility } from '../../../contexts/FooterContext';
import { IFixedAddress } from '../../../types/api';
import { DomEventEnum } from '../../../types/shell';
import { getAddressBodyLines, getAddressHeading, matchesAddressQuery } from '../../../util/address';
import { getPopoverAvailableHeight } from '../../../util/ui';
import { BottomSheet } from './BottomSheet';

// Popper's ModifierArguments; @popperjs/core is transitive via MUI, so not imported.
interface IPopperModifierArgs {
  state: {
    placement: string;
    elements: { reference: { getBoundingClientRect: () => DOMRect } };
  };
}

interface IProps {
  addresses: IFixedAddress[];
  value: string | undefined;
  onChange: (uid: string) => void;
  onBlur?: () => void;
  error?: string;
  isOptionDisabled?: (address: IFixedAddress) => boolean;
}

const ADDRESS_LINE_SX = {
  fontSize: 14,
  lineHeight: 1.43,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

const filterAddresses = (options: IFixedAddress[], query: string) =>
  options.filter((address) => matchesAddressQuery(address, query));

export const AddressBookPicker: React.FC<IProps> = ({
  addresses,
  value,
  onChange,
  onBlur,
  error,
  isOptionDisabled,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isFooterVisible } = useFooterVisibility();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [availableHeight, setAvailableHeight] = useState(POPOVER_MAX_HEIGHT);
  const lastHeightRef = useRef(POPOVER_MAX_HEIGHT);
  const [placement, setPlacement] = useState<PopperPlacementType>('bottom-start');

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const changeButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const selectedAddress = useMemo(() => addresses.find((address) => address.uid === value), [addresses, value]);
  const showSearch = addresses.length > SEARCH_VISIBLE_THRESHOLD;

  const closePopover = useCallback(
    (returnFocus: boolean) => {
      setOpen(false);
      setSearch('');
      onBlur?.();
      if (returnFocus) {
        window.setTimeout(() => changeButtonRef.current?.focus(), 30);
      }
    },
    [onBlur],
  );

  // While the Confirm bar is up, it becomes the bottom edge for flip/preventOverflow.
  const overflowPadding = useMemo(
    () => ({
      top: POPOVER_VIEWPORT_PADDING,
      bottom: isFooterVisible ? POPOVER_FOOTER_CLEARANCE : POPOVER_VIEWPORT_PADDING,
      left: POPOVER_VIEWPORT_PADDING,
      right: POPOVER_VIEWPORT_PADDING,
    }),
    [isFooterVisible],
  );

  const handleOpen = useCallback(() => {
    setSearch('');
    // Measure before the first paint; the size modifier only runs once the popper is mounted, so
    // a stale value from the previous open would otherwise size the Paper on the opening frame.
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      const measure = (isFlipped: boolean) =>
        getPopoverAvailableHeight({
          anchorTop: rect.top,
          anchorBottom: rect.bottom,
          isFlipped,
          paddingTop: overflowPadding.top,
          paddingBottom: overflowPadding.bottom,
          viewportHeight: window.innerHeight,
        });
      const below = measure(false);
      const above = measure(true);
      const flipped = below < POPOVER_MIN_HEIGHT && above > below;
      const next = flipped ? above : below;
      setPlacement(flipped ? 'top-start' : 'bottom-start');
      lastHeightRef.current = next;
      setAvailableHeight(next);
    }
    setOpen(true);
  }, [overflowPadding]);

  // Capture phase catches the embed's inner scroller; scrolls inside the list must not close it.
  useEffect(() => {
    if (!open || isMobile) {
      return undefined;
    }
    // The autofocused search field can scroll an ancestor into view on the opening frame, which
    // arrives as a document-targeted scroll -- ignore that one so the popover survives its own open.
    let armed = false;
    const arm = window.setTimeout(() => {
      armed = true;
    }, 150);
    const handleScroll = (event: Event) => {
      if (!armed || popoverRef.current?.contains(event.target as Node)) {
        return;
      }
      closePopover(false);
    };
    document.addEventListener(DomEventEnum.Scroll, handleScroll, { passive: true, capture: true });

    return () => {
      window.clearTimeout(arm);
      document.removeEventListener(DomEventEnum.Scroll, handleScroll, { capture: true });
    };
  }, [open, isMobile, closePopover]);

  const handlePick = useCallback(
    (uid: string) => {
      onChange(uid);
      closePopover(!isMobile);
    },
    [onChange, closePopover, isMobile],
  );

  // Enter on the highlighted row: the rows are `tabIndex: -1`, so this is the desktop keyboard path.
  const handleAutocompleteChange = useCallback(
    (_: React.SyntheticEvent, picked: IFixedAddress | null) => {
      if (picked?.uid) {
        handlePick(picked.uid);
      }
    },
    [handlePick],
  );

  const handleAutocompleteClose = useCallback(
    (_: React.SyntheticEvent, reason: string) => {
      if (reason === 'escape') {
        closePopover(true);
      }
    },
    [closePopover],
  );

  const handleClearSearch = useCallback(() => {
    setSearch('');
    searchInputRef.current?.focus();
  }, []);

  const renderRow = useCallback(
    (
      address: IFixedAddress,
      { onClick, tabIndex, className, style, ...rowProps }: React.HTMLAttributes<HTMLElement> & { tabIndex?: number },
    ) => {
      const isSelected = address.uid === value;
      const isDisabled = !!isOptionDisabled?.(address);
      const handleRowClick = isDisabled ? undefined : onClick;
      const heading = getAddressHeading(address);
      const addressLine = getAddressBodyLines(address).join(', ');
      const selectedRing = `inset 0 0 0 2px ${theme.palette.primary.main}`;

      return (
        <Box
          // Spread MUI's option props for aria/id/data-option-index/keyboard wiring, but drop
          // className/style so MUI's .MuiAutocomplete-option layout doesn't fight our sx.
          {...rowProps}
          key={address.uid}
          role="option"
          aria-selected={isSelected}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : tabIndex}
          onClick={handleRowClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleRowClick?.(e as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            borderRadius: 1,
            backgroundColor: 'common.white',
            cursor: isDisabled ? 'default' : 'pointer',
            outlineOffset: -2,
            boxShadow: isSelected ? selectedRing : `inset 0 0 0 1px ${theme.palette.divider}`,
            transition: 'box-shadow 150ms cubic-bezier(0.4,0,0.2,1)',
            ...(isDisabled
              ? { opacity: LOCKED_OPACITY, pointerEvents: 'none' }
              : {
                  '&:hover': {
                    boxShadow: isSelected ? selectedRing : `inset 0 0 0 1px ${theme.palette.text.primary}`,
                  },
                }),
          }}
        >
          {isSelected ? (
            <RadioButtonCheckedOutlinedIcon sx={{ fontSize: 22, mt: '1px', color: 'primary.main' }} />
          ) : (
            <RadioButtonUncheckedOutlinedIcon sx={{ fontSize: 22, mt: '1px', color: 'action.active' }} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            {heading && (
              <Typography color="text.primary" fontWeight="medium" sx={{ fontSize: 16, lineHeight: 1.5 }}>
                {heading}
              </Typography>
            )}
            {addressLine && (
              <Typography color="text.secondary" fontWeight="regular" sx={ADDRESS_LINE_SX}>
                {addressLine}
              </Typography>
            )}
          </Box>
        </Box>
      );
    },
    [value, theme, isOptionDisabled],
  );

  const renderSearchField = useCallback(
    (params: AutocompleteRenderInputParams | undefined, autoFocus: boolean, inputId: string) => (
      <TextField
        {...params}
        id={inputId}
        inputRef={searchInputRef}
        fullWidth
        size="medium"
        variant="outlined"
        label="Search addresses"
        autoFocus={autoFocus}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{
          input: {
            ...(params?.InputProps ?? {}),
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 22, color: 'action.active' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                  sx={{ width: 30, height: 30, '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <ClearIcon sx={{ fontSize: 20, color: 'action.active' }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
    ),
    [search, handleClearSearch],
  );

  const emptyState = useMemo(
    () => (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          py: 2.5,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography color="text.secondary" fontWeight="regular" sx={{ fontSize: 14, lineHeight: 1.43 }}>
          No saved addresses match &ldquo;{search}&rdquo;
        </Typography>
        <Button
          variant="text"
          color="primary"
          onClick={handleClearSearch}
          sx={{
            alignSelf: 'flex-start',
            fontSize: 13,
            fontWeight: 'medium',
            lineHeight: '22px',
            p: '4px 5px',
            minWidth: 'auto',
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          Clear search
        </Button>
      </Box>
    ),
    [search, handleClearSearch, theme],
  );

  const collapsedCard = (
    <Box
      sx={{
        backgroundColor: 'common.white',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        ...(error ? { borderColor: 'error.main' } : {}),
      }}
    >
      <LocationOnOutlinedIcon
        sx={{ fontSize: 22, mt: '1px', color: selectedAddress ? 'primary.main' : 'text.disabled' }}
      />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {selectedAddress ? (
          <>
            {getAddressHeading(selectedAddress) && (
              <Typography color="text.primary" fontWeight="medium" sx={{ fontSize: 16, lineHeight: 1.5 }}>
                {getAddressHeading(selectedAddress)}
              </Typography>
            )}
            <Typography color="text.secondary" fontWeight="regular" sx={ADDRESS_LINE_SX}>
              {getAddressBodyLines(selectedAddress).join(', ')}
            </Typography>
          </>
        ) : (
          <>
            <Typography color="text.secondary" fontWeight="medium" sx={{ fontSize: 16, lineHeight: 1.5 }}>
              No address selected
            </Typography>
            <Typography color="text.secondary" fontWeight="regular" sx={{ fontSize: 14, lineHeight: 1.43 }}>
              Choose where your selection should be delivered
            </Typography>
          </>
        )}
      </Box>
      <Button
        ref={changeButtonRef}
        variant="text"
        color="primary"
        onClick={() => (open ? closePopover(false) : handleOpen())}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          mt: '-4px',
          mr: '-5px',
          p: '4px 5px',
          minWidth: 'auto',
          fontSize: 13,
          fontWeight: 'medium',
          lineHeight: '22px',
          whiteSpace: 'nowrap',
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
        }}
      >
        {selectedAddress ? 'Change' : 'Select address'}
      </Button>
    </Box>
  );

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (anchorRef.current?.contains(event.target as Node)) {
        return;
      }
      closePopover(false);
    },
    [closePopover],
  );

  // Stands in for popper v2's missing `size` modifier.
  const sizeModifier = useMemo(
    () => ({
      name: 'addressBookSize',
      enabled: true,
      phase: 'main' as const,
      requiresIfExists: ['offset', 'preventOverflow', 'flip'],
      fn: ({ state }: IPopperModifierArgs) => {
        // Not state.rects — those are offsetParent-relative and drift by scrollY.
        const rect = state.elements.reference.getBoundingClientRect();
        const next = getPopoverAvailableHeight({
          anchorTop: rect.top,
          anchorBottom: rect.bottom,
          isFlipped: state.placement.startsWith('top'),
          paddingTop: overflowPadding.top,
          paddingBottom: overflowPadding.bottom,
          viewportHeight: window.innerHeight,
        });
        // MUI force-updates each render, so an unguarded setState re-enters here.
        if (next !== lastHeightRef.current) {
          lastHeightRef.current = next;
          setAvailableHeight(next);
        }
      },
    }),
    [overflowPadding],
  );

  const popperModifiers = useMemo(
    () => [
      { name: 'flip', enabled: false },
      // Main axis off too: a vertical shift would cover the card without ever making the panel fit.
      { name: 'preventOverflow', enabled: true, options: { mainAxis: false, altAxis: true, padding: overflowPadding } },
      sizeModifier,
    ],
    [overflowPadding, sizeModifier],
  );

  const isPlacedAbove = placement === 'top-start';

  const desktopPopover = !isMobile && open && (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Popper
        open
        anchorEl={anchorRef.current}
        placement={placement}
        // Below the Confirm bar's z-index (20).
        style={{ zIndex: 19, width: anchorRef.current?.offsetWidth }}
        modifiers={popperModifiers}
      >
        <Paper
          ref={popoverRef}
          elevation={4}
          sx={{
            mt: isPlacedAbove ? 0 : 0.5,
            mb: isPlacedAbove ? 0.5 : 0,
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: availableHeight,
          }}
        >
          <Autocomplete<IFixedAddress, false, false, false>
            open
            disablePortal
            openOnFocus
            options={addresses}
            filterOptions={(options, state) => filterAddresses(options, state.inputValue)}
            getOptionLabel={(option) => getAddressHeading(option) ?? ''}
            getOptionDisabled={isOptionDisabled}
            isOptionEqualToValue={(option, selected) => option.uid === selected.uid}
            inputValue={search}
            onInputChange={(_, newValue, reason) => reason === 'input' && setSearch(newValue)}
            value={selectedAddress ?? null}
            onChange={handleAutocompleteChange}
            onClose={handleAutocompleteClose}
            noOptionsText={emptyState}
            // Render the list inline (the Popper is ours), in-flex so maxHeight reaches the listbox.
            slots={{
              popper: ({ children }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children as React.ReactNode}</Box>
              ),
              paper: ({ children }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children as React.ReactNode}</Box>
              ),
            }}
            slotProps={{
              listbox: {
                sx: {
                  maxHeight: 'none',
                  overflowY: 'auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 1.5,
                  m: 0,
                  [`& .${autocompleteClasses.option}`]: { p: 0, backgroundColor: 'transparent' },
                },
              },
            }}
            renderOption={({ key, ...props }, option) => (
              <React.Fragment key={key}>
                {renderRow(option, { ...props, onClick: () => option.uid && handlePick(option.uid) })}
              </React.Fragment>
            )}
            renderInput={(params) =>
              showSearch ? (
                <Box sx={{ pt: 1.5, px: 1.5, pb: 0, flexShrink: 0 }}>
                  {renderSearchField(params, true, 'sp-addr-search-popover')}
                </Box>
              ) : (
                <Box aria-hidden sx={{ height: 0, overflow: 'hidden' }}>
                  {renderSearchField(params, false, 'sp-addr-search-popover')}
                </Box>
              )
            }
          />
        </Paper>
      </Popper>
    </ClickAwayListener>
  );

  return (
    <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
      {collapsedCard}
      {error && (
        <Typography sx={{ fontSize: 12, lineHeight: 1.66, color: 'error.main', mt: '3px', mx: '14px' }}>
          {error}
        </Typography>
      )}
      {desktopPopover}
      {isMobile && (
        // `BottomSheet` uses `disableSwipeToOpen`, so `onOpen` never fires from a gesture;
        // opening is always driven by the "Select address"/"Change" button via `handleOpen`.
        // `height`/`overflow` overrides the component's default `maxHeight: 90vh` so the sheet
        // never resizes while filtering.
        <BottomSheet
          open={open}
          onClose={() => closePopover(false)}
          onOpen={handleOpen}
          sx={{ '& .MuiDrawer-paper': { height: '80%', maxHeight: '80%', overflow: 'hidden' } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Typography fontWeight="medium" sx={{ fontSize: 20, lineHeight: 1.4, mb: 2 }}>
              Choose delivery address
            </Typography>
            {showSearch && (
              <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'grey.100', py: 1.5 }}>
                {renderSearchField(undefined, false, 'sp-addr-search-sheet')}
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5, overflowY: 'auto', minHeight: 0 }}>
              {filterAddresses(addresses, search).length > 0
                ? filterAddresses(addresses, search).map((address) =>
                    renderRow(address, { onClick: () => address.uid && handlePick(address.uid), tabIndex: 0 }),
                  )
                : emptyState}
            </Box>
          </Box>
        </BottomSheet>
      )}
    </Box>
  );
};
